// Works out which API model an uploaded spreadsheet is trying to populate.
//
// Scoring blends two independent signals:
//   1. how many of the model's fields the file's headers actually resolve to
//      (weighted toward REQUIRED fields - a file that covers every required field is
//      far more likely to be that model than one matching a few optional ones), and
//   2. keyword hits in the file name and sheet names, minus antiKeyword hits.
//
// Header evidence dominates deliberately: a file called "May budget positions.xlsx"
// should be classified by its columns, not by whichever noun the user typed first.
import { matchColumns, normalizeColumnName } from '../../../../Helpers';
import { IMPORT_MODELS, ImportFieldDescriptor, ImportModelDescriptor } from './ImportModelRegistry';

/** A model that is confident enough to use without asking gets this far ahead. */
const CONFIDENT_LEAD_RATIO = 2;
/** ...and must also cover at least this share of its required fields. */
const CONFIDENT_REQUIRED_COVERAGE = 0.5;

const REQUIRED_FIELD_WEIGHT = 3;
const OPTIONAL_FIELD_WEIGHT = 1;
const KEYWORD_WEIGHT = 2;
const ANTI_KEYWORD_WEIGHT = 2;

export interface ImportModelScore {
    model: ImportModelDescriptor;
    score: number;
    /** Field keys the file's headers resolved to. */
    matchedFieldKeys: string[];
    /** Required field keys with no matching column - what the user would have to fill in. */
    missingRequiredFieldKeys: string[];
    matchedFieldCount: number;
    totalFieldCount: number;
    requiredFieldCount: number;
}

export interface ImportModelDetection {
    /** Ranked best-first. Always populated so the "ask" UI can show the runners-up. */
    ranked: ImportModelScore[];
    /** Set only when one model is clearly ahead; otherwise the caller should ask. */
    confident: ImportModelScore | null;
}

function countKeywordHits(haystack: string, keywords: readonly string[]): number {
    return keywords.reduce(
        (total, keyword) => total + (haystack.includes(normalizeColumnName(keyword)) ? 1 : 0),
        0
    );
}

function scoreModel(
    model: ImportModelDescriptor,
    columnNames: readonly string[],
    nameHaystack: string
): ImportModelScore {
    // Only score against fields the site actually uses - a site with
    // positionsByLocation off should not be penalised for a file with no Location.
    const fields = model.fields;
    const matches = matchColumns(columnNames, fields);

    const matchedFieldKeys = Array.from(
        new Set(matches.map(m => m.match?.fieldKey).filter((k): k is string => !!k))
    );
    const matchedSet = new Set(matchedFieldKeys);

    // Derived fields (e.g. a budget's pricing strategy) are computed, not read from
    // the file, so a file that lacks them is not thereby a worse match.
    const isRequired = (f: ImportFieldDescriptor) => f.required && !f.derived;
    const requiredFields = fields.filter(isRequired);
    const missingRequiredFieldKeys = requiredFields
        .filter(f => !matchedSet.has(f.key))
        .map(f => f.key);

    let score = 0;
    for (const field of fields) {
        if (!matchedSet.has(field.key)) continue;
        score += isRequired(field) ? REQUIRED_FIELD_WEIGHT : OPTIONAL_FIELD_WEIGHT;
    }

    score += countKeywordHits(nameHaystack, model.keywords) * KEYWORD_WEIGHT;
    score -= countKeywordHits(nameHaystack, model.antiKeywords ?? []) * ANTI_KEYWORD_WEIGHT;

    return {
        model,
        score: Math.max(0, score),
        matchedFieldKeys,
        missingRequiredFieldKeys,
        matchedFieldCount: matchedFieldKeys.length,
        totalFieldCount: fields.length,
        requiredFieldCount: requiredFields.length,
    };
}

/**
 * Rank the known import models against a file's headers and names.
 *
 * `confident` is non-null only when the leader is clearly ahead AND covers enough of
 * its own required fields. Anything less and the caller should ask the user rather
 * than guess - picking the wrong model silently is far worse than one extra click.
 */
export function detectImportModel(
    columnNames: readonly string[],
    options: { fileName?: string; sheetNames?: readonly string[] } = {}
): ImportModelDetection {
    const nameHaystack = normalizeColumnName(
        [options.fileName ?? '', ...(options.sheetNames ?? [])].join(' ')
    );

    const ranked = IMPORT_MODELS
        .map(model => scoreModel(model, columnNames, nameHaystack))
        .sort((a, b) => b.score - a.score);

    const [leader, runnerUp] = ranked;
    let confident: ImportModelScore | null = null;

    if (leader && leader.score > 0) {
        const requiredCoverage = leader.requiredFieldCount === 0
            ? 1
            : (leader.requiredFieldCount - leader.missingRequiredFieldKeys.length) / leader.requiredFieldCount;
        const clearLead = !runnerUp
            || runnerUp.score === 0
            || leader.score >= runnerUp.score * CONFIDENT_LEAD_RATIO;

        if (clearLead && requiredCoverage >= CONFIDENT_REQUIRED_COVERAGE) {
            confident = leader;
        }
    }

    return { ranked, confident };
}
